import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { ORG_TYPES, TIMELINES } from "@/data/catalogue";

// mode: "enquiry" | "interest"
export default function LeadForm({ mode = "enquiry", product }) {
  const isInterest = mode === "interest";
  const [form, setForm] = useState({
    name: "",
    email: "",
    organisation: "",
    org_type: "",
    role: "",
    timeline: "",
    message: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.organisation || !form.org_type) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (!isInterest && !form.message) {
      toast.error("Please tell us briefly what you need.");
      return;
    }
    if (!form.consent) {
      toast.error("Please provide consent to continue.");
      return;
    }
    setLoading(true);
    try {
      const endpoint = isInterest ? "/interest" : "/enquiries";
      const payload = {
        name: form.name,
        email: form.email,
        organisation: form.organisation,
        org_type: form.org_type,
        message: form.message,
        consent: form.consent,
        product_slug: product?.slug || "",
        product_name: product?.name || "",
      };
      if (!isInterest) {
        payload.role = form.role;
        payload.timeline = form.timeline;
      }
      await api.post(endpoint, payload);
      setDone(true);
      toast.success(isInterest ? "Interest registered — check your inbox." : "Enquiry received — check your inbox.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="border border-emerald-500/30 bg-emerald-500/5 p-8">
        <CheckCircle2 className="h-8 w-8 text-emerald-400" strokeWidth={1.5} />
        <h3 className="mt-4 font-display text-xl font-medium">
          {isInterest ? "Interest registered" : "Enquiry received"}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Thank you. We've sent a confirmation to <strong>{form.email}</strong> and our team will
          follow up by email. No account or payment is required.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="lf-name">Full name *</Label>
          <Input
            id="lf-name"
            data-testid="lead-name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="rounded-sm"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lf-email">Work email *</Label>
          <Input
            id="lf-email"
            type="email"
            data-testid="lead-email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="rounded-sm"
          />
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="lf-org">Organisation *</Label>
          <Input
            id="lf-org"
            data-testid="lead-org"
            value={form.organisation}
            onChange={(e) => set("organisation", e.target.value)}
            className="rounded-sm"
          />
        </div>
        <div className="space-y-2">
          <Label>Organisation type *</Label>
          <Select value={form.org_type} onValueChange={(v) => set("org_type", v)}>
            <SelectTrigger data-testid="lead-org-type" className="rounded-sm">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {ORG_TYPES.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isInterest && (
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="lf-role">Your role</Label>
            <Input
              id="lf-role"
              data-testid="lead-role"
              value={form.role}
              onChange={(e) => set("role", e.target.value)}
              className="rounded-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Timeline</Label>
            <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
              <SelectTrigger data-testid="lead-timeline" className="rounded-sm">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {TIMELINES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="lf-msg">
          {isInterest ? "What would you like this product to do? (optional)" : "How can we help? *"}
        </Label>
        <Textarea
          id="lf-msg"
          data-testid="lead-message"
          rows={4}
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          className="rounded-sm"
        />
      </div>

      <label className="flex cursor-pointer items-start gap-3 border border-border bg-secondary/50 p-4">
        <Checkbox
          data-testid="lead-consent"
          checked={form.consent}
          onCheckedChange={(v) => set("consent", !!v)}
          className="mt-0.5"
        />
        <span className="text-xs leading-relaxed text-muted-foreground">
          I consent to BIGTEK storing the details in this form for the sole purpose of responding to
          this {isInterest ? "interest registration" : "enquiry"}. I understand my data is not sold,
          not used for AI training, and not shared with third-party advertisers.
        </span>
      </label>

      <Button
        type="submit"
        data-testid="lead-submit"
        disabled={loading}
        className="w-full rounded-sm bg-[hsl(var(--brand))] py-6 text-white hover:bg-[hsl(217_91%_53%)] sm:w-auto sm:px-10"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…
          </>
        ) : isInterest ? (
          "Register interest"
        ) : (
          "Send enquiry"
        )}
      </Button>

      <p className="flex items-start gap-2 text-xs leading-relaxed text-slate-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
        Minimal fields only. We store your submission for up to 24 months to manage the engagement,
        then delete it. No third-party analytics run on this form. You can request access or deletion
        at any time by emailing bigtek@bigibila.com.au.
      </p>
    </form>
  );
}
